/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import merge from 'deepmerge';
import { type ItemStyleOption } from 'echarts/types/src/util/types';
import { type CustomSeriesRenderItemAPI } from 'echarts';
import { GraphicType, type Graphic, type DvScatterData } from '../type';
import { combineMerge } from '../../../util/array';
import { type Option } from '../../../../core/option';
import type { Scale } from '../../../../helper/dvScatter';

// 约定每个图元(外层Group除外))的命名方式: [业务类型]+[图形类型]

// 只有主图元触发点击事件
enum CircleGraphics {
  group = 'group',
  circle = 'circle',
  image = 'image',
  text = 'text',
  line = 'line',
  badgeGroup = 'group',
  badgeCircle = 'circle',
  badgeText = 'text'
}

const SIZE_RULE = {
  baseSize: 24,
  baseTextSize: 16,
  baseBadgeSize: 14,
  baseBadgeTextSize: 12,
  textScale: 0.5,
  badgeSizeScale: 0.25,
  badgeTextScale: 0.25
};
/**
 * @rule named graphic by pascal in order to distinguish between graphics and config
 * like config [text] and graphic [text]
 */
export function circle(
  option: Option,
  data: DvScatterData,
  config: {
    scale: {
      xScale: Scale;
      yScale: Scale;
      rScale: Scale;
    };
    api: CustomSeriesRenderItemAPI;
  }
): Graphic {
  const { scale, api } = config;
  const { xScale, yScale, rScale } = scale;
  const {
    itemConfig = {},
    itemStyle = [],
    value = [],
    formatValue = [],
    formatNextValue = [],
    formatPrevValue = [],
    z = -1
  } = data;
  const baseStyle = api.style({
    stroke: api.visual('color'),
    fill: api.visual('color')
  });
  const point = api.coord(formatValue);
  const size = rScale(value[2]);
  // eslint-disable-next-line no-magic-numbers
  const radius = size / 2;
  const baseScale = size / SIZE_RULE.baseSize;

  const { dvItemConfig: globalConfig = {}, dvItemStyle: globalStyle = [] } = option.series;

  const finalConfig = merge(globalConfig, itemConfig);
  const finalStyle = merge(globalStyle, itemStyle, { arrayMerge: combineMerge('id') });
  const styleMap = {};
  finalStyle.forEach(({ id, ...other }) => {
    styleMap[id] = other;
  });

  let Group: Graphic = {
    name: CircleGraphics.group,
    type: GraphicType.group,
    x: point[0],
    y: point[1],
    enterFrom: { scaleX: 0, scaleY: 0 },
    leaveTo: { scaleX: 0, scaleY: 0 },
    enterAnimation: {
      duration: 500
    },
    leaveAnimation: {
      duration: 500
    },
    children: [],
    diffChildrenByName: true
  };
  Group = merge(Group, styleMap[CircleGraphics.group] || {});

  // base graphic
  let Circle: Graphic = {
    id: CircleGraphics.circle,
    name: CircleGraphics.circle,
    type: GraphicType.circle,
    // 标记为主图元
    extra: {
      isMain: true
    },
    shape: {
      cx: 0,
      cy: 0,
      r: radius
    },
    style: baseStyle,
    transition: 'shape',
    textContent: {
      // label标记位
      extra: {
        isLabel: true
      },
      style: {
        text: baseStyle.text
      }
    },
    z2: z
  };
  Circle = merge(Circle, styleMap[CircleGraphics.circle] || {});
  Group.children.push(Circle);

  // extend graphic
  if (formatNextValue.length >= 2) {
    const nextPoint = api.coord(formatNextValue);
    let Line: Graphic = {
      id: CircleGraphics.line,
      name: CircleGraphics.line,
      type: GraphicType.line,
      silent: true,
      shape: {
        x1: 0,
        y1: 0,
        x2: nextPoint[0] - point[0],
        y2: nextPoint[1] - point[1]
      },
      enterFrom: { scaleX: 0, scaleY: 0, shape: { x1: 0, y1: 0, x2: 0, y2: 0 } },
      leaveTo: { scaleX: 0, scaleY: 0, shape: { x1: 0, y1: 0, x2: 0, y2: 0 } },
      style: { lineWidth: 1, stroke: Circle.style.fill },
      // disable label
      textContent: {
        style: {
          text: ''
        }
      },
      z2: z
    };
    Line = merge(Line, styleMap[CircleGraphics.line] || {});
    Group.children.unshift(Line);
  }

  if (formatPrevValue.length >= 2) {
    // 传入该参数，改变气泡为外接全等三角形
    const prePoint = api.coord(formatPrevValue);
    const radian = Math.atan2(prePoint[1] - point[1], point[0] - prePoint[0]);
    const angle60 = Math.PI / 3;
    Circle.rotation = radian;
    Circle.type = 'polygon';
    Circle.shape = {
      points: [
        [radius / Math.cos(angle60), 0],
        [-radius, radius * Math.tan(angle60)],
        [-radius, -radius * Math.tan(angle60)]
      ]
    };
  }

  const { image = '', badgeText = '', insideText = '' } = finalConfig;
  if (image) {
    const borderWidth = (Circle.style?.lineWidth || 0) as number;
    // eslint-disable-next-line no-magic-numbers
    const twiceBorderWidth = borderWidth * 2;
    let Image: Graphic = {
      id: CircleGraphics.image,
      name: CircleGraphics.image,
      type: GraphicType.image,
      silent: true,
      style: {
        image,
        x: -radius + borderWidth,
        y: -radius + borderWidth,
        width: size - twiceBorderWidth,
        height: size - twiceBorderWidth
      },
      // disable label
      textContent: {
        style: {
          text: ''
        }
      },
      z2: z
    };
    Image = merge(Image, styleMap[CircleGraphics.image] || {});
    Group.children.push(Image);
  }

  if (insideText) {
    let Text: Graphic = {
      id: CircleGraphics.text,
      name: CircleGraphics.text,
      type: GraphicType.text,
      silent: true,
      style: {
        x: 0,
        y: 0,
        text: insideText,
        fontSize: SIZE_RULE.baseTextSize * SIZE_RULE.textScale * baseScale,
        textAlign: 'center',
        textVerticalAlign: 'middle'
      },
      z2: 9999999
    };
    Text = merge(Text, styleMap[CircleGraphics.text] || {});
    Group.children.push(Text);
  }

  if (badgeText) {
    // eslint-disable-next-line no-magic-numbers
    const arc45 = Math.PI / 4;
    let Badge: Graphic = {
      id: CircleGraphics.badgeGroup,
      name: CircleGraphics.badgeGroup,
      type: GraphicType.group,
      silent: true,
      x: radius * Math.cos(arc45),
      y: -radius * Math.sin(arc45),
      children: []
    };
    Badge = merge(Badge, styleMap[CircleGraphics.badgeGroup] || {});

    let BadgeCircle: Graphic = {
      id: CircleGraphics.badgeCircle,
      name: CircleGraphics.badgeCircle,
      type: GraphicType.circle,
      shape: {
        cx: 0,
        cy: 0,
        r: SIZE_RULE.baseBadgeSize * SIZE_RULE.badgeSizeScale * baseScale
      },
      style: { fill: Circle.style.fill },
      // disable label
      textContent: {
        style: {
          text: ''
        }
      },
      z2: z
    };
    BadgeCircle = merge(BadgeCircle, styleMap[CircleGraphics.badgeCircle] || {});

    let BadgeText: Graphic = {
      id: CircleGraphics.badgeText,
      name: CircleGraphics.badgeText,
      type: GraphicType.text,
      style: {
        x: 0,
        y: 0,
        text: badgeText,
        fontSize: SIZE_RULE.baseBadgeTextSize * SIZE_RULE.badgeTextScale * baseScale,
        textAlign: 'center',
        textVerticalAlign: 'middle'
      },
      z2: z
    };
    BadgeText = merge(BadgeText, styleMap[CircleGraphics.badgeText] || {});
    Badge.children.push(BadgeCircle);
    Badge.children.push(BadgeText);
    Group.children.push(Badge);
  }
  return Group;
}
